import { cn } from "@/lib/utils";

/**
 * Moldura compartilhada pelos três previews da seção de recursos da landing.
 *
 * O ponto da moldura é justamente ser sempre a mesma: sidebar, topbar e
 * cartões idênticos em todas as telas. Quem lê a seção aprende a interface uma
 * vez e reconhece nas outras duas — o oposto dos previews antigos, que tinham
 * uma linguagem visual diferente por módulo (telefone, ingresso, PDV) e faziam
 * o produto parecer três produtos.
 *
 * Os itens da sidebar espelham os módulos de topo reais de `useNavItems`, na
 * mesma ordem — se um módulo entrar ou sair de lá, atualize aqui.
 *
 * Dois tamanhos, não um. No desktop a moldura tem ~560px e o texto miúdo
 * (8–10px) lê como "print de tela", que é o efeito desejado. No mobile ela
 * ocupa os 343px inteiros e o mesmo texto fica ilegível — e um print ilegível
 * de software denso comunica o oposto do que a seção quer provar. Por isso a
 * tipografia sobe um degrau abaixo do `md` e as telas mostram MENOS coisa:
 * indicador a menos, linha a menos. A altura sai igual, e vale mais uma tela
 * que se lê do que uma tela completa que não se lê. Ao acrescentar conteúdo
 * aqui, decida primeiro o que sai no mobile.
 */
const NAV_ITEMS = [
  "Dashboard",
  "Pessoas",
  "Ministérios",
  "Finanças",
  "Kids",
  "Atividades",
  "Cantina",
  "Relatórios",
  "Bio Links",
  "Configurações",
] as const;

export type NavItem = (typeof NAV_ITEMS)[number];

interface AppFrameProps {
  /** Módulo destacado na sidebar. */
  active: NavItem;
  /** Trilha exibida na topbar, ex.: `["Início", "Finanças"]`. */
  breadcrumb: string[];
  /** Título da página, dentro da área de conteúdo. */
  title: string;
  /** Linha de apoio abaixo do título. */
  subtitle: string;
  children: React.ReactNode;
}

export function AppFrame({
  active,
  breadcrumb,
  title,
  subtitle,
  children,
}: AppFrameProps) {
  return (
    <div className="flex w-full h-full bg-background text-foreground overflow-hidden">
      {/* ── Sidebar ─────────────────────────────────────────────────── */}
      <div className="hidden md:flex w-31 shrink-0 border-r border-border/30 flex-col py-3 gap-0.5">
        <div className="px-2.5 pb-3 mb-1 flex items-center gap-1.5">
          <div className="w-6 h-6 rounded-lg bg-primary/20 grid place-items-center text-primary font-bold text-[10px] shrink-0">
            E
          </div>
          <p className="text-[9px] font-semibold text-foreground truncate leading-tight min-w-0">
            Central da Esperança
          </p>
        </div>

        <p className="px-2.5 text-[8px] text-muted-foreground/50 uppercase tracking-widest font-medium mb-1">
          Navegação
        </p>

        {NAV_ITEMS.map((item) => (
          <div
            key={item}
            className={cn(
              "mx-1.5 px-2 py-1.5 rounded-md text-[10px] font-medium",
              item === active
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground/70",
            )}
          >
            {item}
          </div>
        ))}
      </div>

      {/* ── Conteúdo ────────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-border/20">
          {breadcrumb.map((crumb, i) => (
            <span
              key={crumb}
              className={cn(
                "text-[11px] md:text-[10px]",
                i === breadcrumb.length - 1
                  ? "text-muted-foreground"
                  : "text-muted-foreground/40",
              )}
            >
              {i > 0 && <span className="mr-1.5 text-border">/</span>}
              {crumb}
            </span>
          ))}
        </div>

        <div className="flex-1 p-4 flex flex-col gap-3">
          <div>
            <p className="text-sm font-semibold text-foreground leading-none">
              {title}
            </p>
            <p className="text-[11px] md:text-[10px] text-muted-foreground mt-1">
              {subtitle}
            </p>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

/* ── Peças reaproveitadas pelas três telas ─────────────────────────── */

export function StatCard({
  label,
  value,
  sub,
  tone = "default",
  className,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: "default" | "positive" | "alert";
  /** Use `"hidden md:flex"` no indicador que sai no mobile. */
  className?: string;
}) {
  return (
    <div
      className={cn(
        "bg-card rounded-xl border border-border/40 p-2.5 flex flex-col gap-0.5 min-w-0",
        className,
      )}
    >
      <p className="text-[11px] md:text-[9px] text-muted-foreground font-medium truncate">
        {label}
      </p>
      <p
        className={cn(
          "text-lg font-bold leading-none",
          tone === "positive" && "text-status-good",
          tone === "alert" && "text-status-warning",
          tone === "default" && "text-foreground",
        )}
      >
        {value}
      </p>
      <p className="text-[11px] md:text-[9px] text-muted-foreground truncate">
        {sub}
      </p>
    </div>
  );
}

export function Panel({
  title,
  action,
  children,
  className,
}: {
  title: string;
  action?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "bg-card rounded-xl border border-border/40 p-3 flex flex-col gap-2 flex-1 min-h-0",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-[12px] md:text-[10px] font-semibold text-foreground">
          {title}
        </p>
        {action && (
          <span className="text-[11px] md:text-[9px] text-primary/70 shrink-0">
            {action}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

export function Row({
  avatar,
  title,
  sub,
  chip,
  chipTone = "neutral",
  className,
}: {
  avatar: string;
  title: string;
  sub: string;
  chip: string;
  chipTone?: "neutral" | "positive" | "alert";
  /** Use `"hidden md:flex"` na linha que sai no mobile. */
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 px-2 py-1.5 rounded-lg bg-secondary/25",
        className,
      )}
    >
      <div className="w-7 h-7 md:w-6 md:h-6 rounded-full bg-primary/10 text-primary grid place-items-center text-[10px] md:text-[9px] font-bold shrink-0">
        {avatar}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[12px] md:text-[10px] font-medium text-foreground truncate">
          {title}
        </p>
        <p className="text-[11px] md:text-[9px] text-muted-foreground truncate">
          {sub}
        </p>
      </div>
      <span
        className={cn(
          "text-[10px] md:text-[8px] px-1.5 py-0.5 rounded-full font-medium shrink-0",
          chipTone === "positive" &&
            "bg-status-good/10 text-status-good",
          chipTone === "alert" &&
            "bg-status-warning/10 text-status-warning",
          chipTone === "neutral" && "bg-secondary text-muted-foreground",
        )}
      >
        {chip}
      </span>
    </div>
  );
}
